/**
 * Unlock Condition Evaluation
 * 
 * Supports two formats:
 * 
 * V2 (block-based): { version: 2, blocks: [{ conditions, innerOperator, outerOperator }] }
 *   - Each block's conditions are combined with innerOperator (AND/OR)
 *   - Blocks are combined with outerOperator (AND/OR), evaluated left-to-right
 * 
 * V1 (flat array): [{ taskId, operator }]
 *   - Auto-converted to V2 for evaluation
 */

import { isTaskEffectivelyCompleted } from './helpers';
import { isWaitFulfilled, isWaitNode, waitKindLabel } from '../../../../../utils/waitFor';
import { isV2Format, normalizeToV2 } from '../../../shared/ConditionsDropdown/utils/conditionHelpers';
import { getConditionAvailableAtMs, getWaitSeconds } from '../../../shared/ConditionsDropdown/utils/conditionWait';

/**
 * Evaluate a single condition against the task map
 */
function evaluateCondition(condition, taskMap, nestedSprints, statuses, now) {
  const task = taskMap.get(condition.taskId);
  let isSatisfied = false;
  let reason = 'not_completed';
  let requiredBranch = null;
  let remainingMs = null;
  let availableAt = null;

  if (task) {
    if (isWaitNode(task)) {
      isSatisfied = isWaitFulfilled(task);
      if (!isSatisfied) reason = 'wait_unfulfilled';
    } else if (task.nodeType === 'decision' && condition.branch) {
      const chosenAnswer = task.answer || task.selectedOption;
      isSatisfied = chosenAnswer === condition.branch;
      if (!isSatisfied) {
        const branchOption = task.options?.find(o => o.id === condition.branch);
        requiredBranch = branchOption?.label || branchOption?.name || 'specific choice';
        reason = chosenAnswer ? 'wrong_branch' : 'no_choice_made';
      }
    } else {
      isSatisfied = isTaskEffectivelyCompleted(task, nestedSprints, statuses);
    }

    if (isSatisfied && getWaitSeconds(condition)) {
      const availableAtMs = getConditionAvailableAtMs(condition, task);
      if (availableAtMs != null && now < availableAtMs) {
        isSatisfied = false;
        reason = 'waiting_until';
        remainingMs = availableAtMs - now;
        availableAt = availableAtMs;
      }
    }
  }

  return { isSatisfied, reason, requiredBranch, task, remainingMs, availableAt };
}

/**
 * Evaluate unlock conditions with AND/OR logic (supports v1 and v2 formats)
 * 
 * @param {Array|Object} conditions - V1 array or V2 object
 * @param {Map} taskMap - Map of taskId -> task
 * @param {Array} nestedSprints - Nested sprints array
 * @param {Array} statuses - Custom statuses array
 * @returns {Object} { isSatisfied, unsatisfiedTasks }
 */
export function evaluateUnlockConditions(conditions, taskMap, nestedSprints, statuses, now = Date.now()) {
  if (!conditions) {
    return { isSatisfied: true, unsatisfiedTasks: [] };
  }

  // Normalize to v2
  const v2 = isV2Format(conditions) ? conditions : normalizeToV2(conditions);

  if (!v2.blocks || v2.blocks.length === 0) {
    return { isSatisfied: true, unsatisfiedTasks: [] };
  }

  // Evaluate each block
  const blockResults = v2.blocks.map(block => {
    const validConditions = (block.conditions || []).filter(c => c.taskId && taskMap.has(c.taskId));
    if (validConditions.length === 0) {
      return { isSatisfied: true, unsatisfiedTasks: [], outerOperator: block.outerOperator };
    }

    const results = validConditions.map(cond => {
      const evalResult = evaluateCondition(cond, taskMap, nestedSprints, statuses, now);
      return { ...cond, ...evalResult };
    });

    const innerOp = block.innerOperator || 'AND';
    const blockSatisfied = innerOp === 'AND'
      ? results.every(r => r.isSatisfied)
      : results.some(r => r.isSatisfied);

    const unsatisfied = results
      .filter(r => !r.isSatisfied && r.task)
      .map(r => ({
        id: r.task.id,
        name: r.reason === 'wait_unfulfilled'
          ? (r.task.name || `Wait for ${waitKindLabel(r.task.waitFor).toLowerCase()}`)
          : r.task.name,
        status: r.task.status,
        reason: r.reason,
        requiredBranch: r.requiredBranch,
        remainingMs: r.remainingMs,
        availableAt: r.availableAt,
        waitSeconds: getWaitSeconds(r),
      }));

    return {
      isSatisfied: blockSatisfied,
      unsatisfiedTasks: unsatisfied,
      outerOperator: block.outerOperator,
      innerOperator: innerOp,
      totalConditions: validConditions.length,
      satisfiedCount: results.filter(r => r.isSatisfied).length,
    };
  });

  // Combine block results using outerOperator, left-to-right
  let overallSatisfied = blockResults[0].isSatisfied;

  for (let i = 1; i < blockResults.length; i++) {
    const op = blockResults[i].outerOperator || 'AND';
    if (op === 'AND') {
      overallSatisfied = overallSatisfied && blockResults[i].isSatisfied;
    } else {
      overallSatisfied = overallSatisfied || blockResults[i].isSatisfied;
    }
  }

  if (overallSatisfied) {
    return { isSatisfied: true, unsatisfiedTasks: [] };
  }

  return {
    isSatisfied: false,
    unsatisfiedTasks: getBlockingTasks(blockResults),
  };
}

/**
 * Determine which tasks to show as blocking.
 * Finds the "closest to done" path through the block logic.
 */
function getBlockingTasks(blockResults) {
  const allUnsatisfied = [];

  for (const block of blockResults) {
    if (!block.isSatisfied) {
      allUnsatisfied.push(...block.unsatisfiedTasks);
    }
  }

  if (allUnsatisfied.length === 0) return [];

  // Deduplicate by task id
  const seen = new Set();
  return allUnsatisfied.filter(t => {
    if (seen.has(t.id)) return false;
    seen.add(t.id);
    return true;
  });
}
